import { FC, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AuthWrapper from '../../common/AuthWrapper';
import CustomInput from '../../common/CustomInput';
import CustomButton from '../../common/CustomButton';
import { useIsMobile } from '../../hooks/useWindowWidth';
import {
  useLoginUserConfirmMutation,
  useLoginUserMutation,
  useSignUpResendCodeMutation,
  useValidateCodeMutation,
} from '../../store/services/authApi';
import { useAppSelector } from '../../hooks/redux';
import styles from './CodeValidation.module.scss';
import { useDispatch } from 'react-redux';
import { setCredentials, setFirstLoginStep } from '../../store/slices/authSlice';
const PENDING_SIGN_IN = 'sign-in';

const CodeValidation: FC = () => {
  const isMobile = useIsMobile();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [validateCode] = useValidateCodeMutation();
  const [loginUserConfirmMutation] = useLoginUserConfirmMutation();
  const [loginUser] = useLoginUserMutation();
  const [signUpResendCode] = useSignUpResendCodeMutation();
  const { firstLoginStep } = useAppSelector(state => state.auth);
  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const isSignInVerification = firstLoginStep || sessionStorage.getItem('pendingCodeVerification') === PENDING_SIGN_IN;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  useEffect(() => {
    if (token) {
      localStorage.setItem('resetPassToken', token);
    }
  }, [token]);

  const initialValues = {
    validationCode: token ?? '',
  };

  const isRegistrationVerification = sessionStorage.getItem('postCodeVerificationRoute') === 'onboarding';

  const handleResendCode = async () => {
    if (!isSignInVerification) return;

    const email = localStorage.getItem('userEmail');
    if (!email) {
      setResendMessage('Email not found. Please start sign in or sign up again.');
      return;
    }

    setResendMessage(null);
    setIsResending(true);
    try {
      if (isRegistrationVerification) {
        await signUpResendCode({ email }).unwrap();
      } else {
        let password = sessionStorage.getItem('pendingLoginPassword') || '';
        if (!password) {
          try {
            const raw = localStorage.getItem('createAccount');
            if (raw) {
              password = JSON.parse(raw).password ?? '';
            }
          } catch {
            /* ignore */
          }
        }

        if (!password) {
          setResendMessage('Unable to resend code. Please go back and enter your password again.');
          return;
        }

        await loginUser({ email, password }).unwrap();
      }
      setResendMessage('A verification code has been sent and is valid for 30 minutes');
      setResErrors(null);
    } catch (error) {
      setResErrors((error as { data?: { errors?: typeof resErrors } })?.data?.errors ?? null);
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (values: { validationCode: string }) => {
    if (isSignInVerification) {
      try {
        const result = await loginUserConfirmMutation({
          email: localStorage.getItem('userEmail'),
          confirmation_code: values.validationCode,
        }).unwrap();
        dispatch(setCredentials({ ...result }));
        if (result.access) {
          localStorage.setItem('authToken', result.access);
        }
        dispatch(setFirstLoginStep(false));
        sessionStorage.removeItem('pendingCodeVerification');
        sessionStorage.removeItem('pendingLoginPassword');
        const afterVerify = sessionStorage.getItem('postCodeVerificationRoute');
        sessionStorage.removeItem('postCodeVerificationRoute');
        if (afterVerify === 'onboarding') {
          navigate('/personalized-form', { replace: true });
        } else {
          navigate('/profile', { replace: true });
        }
        setResErrors(null);
      } catch (error) {
        console.error('Error logging in:', error);
        setResendMessage(null);
        setResErrors((error as { data?: { errors?: typeof resErrors } })?.data?.errors || null);
      }
    } else {
      try {
        await validateCode({ token: values.validationCode }).unwrap();
        navigate('/create-new-password');
        setResErrors(null);
      } catch (error) {
        console.error(error);
        setResendMessage(null);
        setResErrors((error as { data?: { errors?: typeof resErrors } })?.data?.errors || null);
      }
    }
  };

  const formikValidate = (values: { validationCode: string }) => {
    const errors: { validationCode?: string } = {};
    return errors;
  };

  return (
    <AuthWrapper btnName="Sign In">
      <Box className={styles.contentWrapper}>
        <Box className={styles.validationCodeContent}>
          <Typography
            variant={`${isMobile ? 'h6Bold' : 'h5Bold'}`}
            className={styles.title}
          >
            Verify your email address
          </Typography>
          <Typography className={styles.subtitle}>Enter the code we sent you by email</Typography>

          <Formik
            initialValues={initialValues}
            validate={formikValidate}
            onSubmit={handleSubmit}
          >
            {({ values, errors, handleChange, isSubmitting }) => (
              <Form className={styles.validationCodeForm}>
                <CustomInput
                  inputType="text"
                  inputName="validationCode"
                  inputLabel="Code Verification"
                  placeholder="Enter code"
                  value={values.validationCode}
                  error={errors.validationCode}
                  customClass={styles.customInput}
                  onChange={handleChange}
                />

                <CustomButton
                  btnType="submit"
                  customClass={styles.customButton}
                  disabled={isSubmitting}
                >
                  Verify code
                </CustomButton>

                {isSignInVerification && (
                  <CustomButton
                    btnType="button"
                    variant="outlined"
                    customClass={styles.resendButton}
                    disabled={isResending}
                    onClick={handleResendCode}
                  >
                    Resend Code
                  </CustomButton>
                )}

                {resendMessage && (
                  <Typography
                    variant="h12"
                    sx={{ color: '#208B64', mt: 1 }}
                  >
                    {resendMessage}
                  </Typography>
                )}

                {resErrors &&
                  resErrors.map((error, index) => (
                    <Typography
                      key={index}
                      variant="h12"
                      sx={{ color: '#C8102E' }}
                    >
                      {error.detail}
                    </Typography>
                  ))}
              </Form>
            )}
          </Formik>
        </Box>
        <Box className={styles.termsAndConditions}>
          <Typography
            variant="h11"
            sx={{ textAlign: 'center' }}
          >
            By signing up, you understand and agree with TravGenius'
          </Typography>
          <Typography variant="h11">
            <a
              href="/terms-privacy/TravGenius Terms and Conditions MAIN.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#222B3A' }}
            >
              Terms & Conditions
            </a>
            <span> and </span>
            <a
              href="/terms-privacy/TravGenius Privacy Policy (GDPR & CCPA) MAIN.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#222B3A' }}
            >
              Privacy Policy
            </a>
          </Typography>
        </Box>
      </Box>
    </AuthWrapper>
  );
};

export default CodeValidation;
